import LogoutIcon from '@mui/icons-material/DeleteForever';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import { useContext, useMemo, useState } from 'react';
import { deleteOwnAccount } from '../api/auth';
import { AccountContext } from '../context/account';
import { ErrorMessage } from './error-message';
import Loader from './loader';
import { ModalTitle } from './modal-title';

interface DeleteAccountModalProps {
  open: boolean;
  onClose: () => void;
  onDeleted: () => void; // parent logs out + redirects
}

export const DeleteAccountModal = ({ open, onClose, onDeleted }: DeleteAccountModalProps) => {
  const { account } = useContext(AccountContext);
  // account.organisation is the user's org list (same shape used elsewhere).
  const { first_name, organisation } = account;

  const [typedName, setTypedName] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string[]>([]);

  // PRE-FLIGHT (client-side, primary UX): is the user the sole admin of any org?
  // Mirrors the server guard so we can block BEFORE they fill the form. The
  // server re-checks regardless.
  const lastAdminOrgs = useMemo(() => {
    const orgs = Array.isArray(organisation) ? organisation : [];
    // We only have the user's own role per org here (role: 'admin' | 'member' | ...),
    // not the full member list, so we can't count other admins from this alone.
    // Where the org's admin count isn't available client-side, we surface the
    // orgs where the user IS an admin and let the SERVER make the final call.
    return orgs.filter((o: any) => o.role === 'admin');
  }, [organisation]);

  // First-name confirm, case-insensitive, trimmed (UK: first name only).
  const nameMatches =
    !!first_name && typedName.trim().toLowerCase() === String(first_name).trim().toLowerCase();

  const canSubmit = nameMatches && password.length > 0 && !loading;

  const reset = () => {
    setTypedName('');
    setPassword('');
    setError([]);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const onConfirm = () => {
    setError([]);
    setLoading(true);
    const execute = async () => {
      const { error: status, message } = await deleteOwnAccount({ password });
      setLoading(false);
      if (status !== 200) {
        // 401 wrong password, 409 last-admin block, etc. — show what server says.
        setError((e) => [...e, `${message}`]);
      } else {
        onDeleted();
      }
    };
    execute();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <ModalTitle onClose={handleClose}>Delete your account</ModalTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <DialogContentText>
          This <strong>permanently deletes your account</strong> and cannot be undone. Your personal
          details will be removed. Purchase and invoice records are retained as required by law.
        </DialogContentText>

        {lastAdminOrgs.length > 0 &&
          <DialogContentText sx={{ color: 'warning.main' }}>
            You are an admin of {lastAdminOrgs.length === 1 ? 'an organisation' : 'organisations'}.
            If you are the last admin, deletion will be blocked — promote another admin or leave first.
          </DialogContentText>
        }

        <DialogContentText>
          To confirm, type your first name <strong>{first_name}</strong> and your password.
        </DialogContentText>

        <FormControl>
          <TextField
            id='delete-account-first-name'
            label="Your first name"
            value={typedName}
            onChange={(e) => setTypedName(e.currentTarget.value)}
            autoComplete="off"
            fullWidth
          />
        </FormControl>

        <FormControl>
          <TextField
            id='delete-account-password'
            label="Your password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            autoComplete="current-password"
            fullWidth
          />
        </FormControl>

        <ErrorMessage message={error} />
      </DialogContent>

      <DialogActions sx={{ pb: 3, px: 3 }}>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          id="delete-account-confirm"
          variant="contained"
          color="error"
          startIcon={loading ? <Loader small /> : <LogoutIcon sx={{ color: '#fff' }} />}
          disabled={!canSubmit}
          onClick={onConfirm}
        >
          {loading ? 'Deleting…' : 'Delete my account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};